import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Shield, Copy, Check, MapPin, Loader2, Flag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import OriginPicker from "@/components/OriginPicker";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  active: { label: "On the way", color: "#16a34a", bg: "#16a34a15" },
  arrived: { label: "Arrived safely", color: "#16a34a", bg: "#16a34a15" },
  stalled: { label: "Not moving — check in", color: "#F59E0B", bg: "#F59E0B15" },
  overdue: { label: "Taking longer than expected", color: "#dc2626", bg: "#dc262615" },
};

export default function WalkWithFriend() {
  const { user } = useAuth();
  const [dest, setDest] = useState(null);
  const [walk, setWalk] = useState(null);
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  const center = user?.school_center;

  useEffect(() => {
    api.get("/walks/active").then((r) => { if (r.data) resume(r.data); }).catch(() => {});
    return () => clearInterval(timer.current);
  }, []);

  const resume = (w) => { setWalk({ id: null, token: w.token }); setStatus(w); startPinging(null); };

  const shareUrl = walk ? `${window.location.origin}/walk/w/${walk.token}` : "";

  const ping = async (id) => {
    navigator.geolocation?.getCurrentPosition(async (pos) => {
      try {
        const r = await api.post(`/walks/${id}/ping`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus(r.data);
        if (r.data.status === "arrived") { clearInterval(timer.current); }
      } catch {}
    }, () => {}, { enableHighAccuracy: true, timeout: 8000 });
  };

  const startPinging = (id) => {
    clearInterval(timer.current);
    if (!id) return;
    ping(id);
    timer.current = setInterval(() => ping(id), 6000);
  };

  const start = () => {
    if (!dest) { toast.error("Pick where you're headed first."); return; }
    setStarting(true);
    navigator.geolocation?.getCurrentPosition(async (pos) => {
      try {
        const r = await api.post("/walks", {
          to_lat: dest.lat, to_lng: dest.lng, to_label: dest.label,
          from_lat: pos.coords.latitude, from_lng: pos.coords.longitude,
        });
        setWalk(r.data);
        startPinging(r.data.id);
        toast.success("Walk started — share the link with a friend.");
      } catch { toast.error("Could not start walk."); } finally { setStarting(false); }
    }, () => { toast.error("Enable location to start a walk."); setStarting(false); },
    { enableHighAccuracy: true, timeout: 8000 });
  };

  const arrive = async () => {
    if (walk?.id) { try { await api.post(`/walks/${walk.id}/stop`); } catch {} }
    clearInterval(timer.current);
    setStatus((s) => ({ ...(s || {}), status: "arrived" }));
    toast.success("Marked as arrived. Stay safe!");
  };

  const copy = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const st = STATUS_STYLE[status?.status] || STATUS_STYLE.active;

  return (
    <AppShell>
      <div className="px-4 lg:px-6 py-5 max-w-xl" data-testid="walk-page">
        <div className="mb-5 pr-16 lg:pr-20">
          <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand flex items-center gap-1.5">
            <Shield size={12} strokeWidth={1.5} /> Safety
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Walk with a friend</h1>
          <p className="text-sm text-muted-foreground mt-1">Share your live location & ETA. Your friend gets an alert if you stop or run late.</p>
        </div>

        {!walk ? (
          <div className="bg-white border border-border rounded-xl p-5 space-y-4">
            <div>
              <span className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">Where are you headed?</span>
              <div className="mt-1.5">
                <OriginPicker center={center} value={dest} onChange={setDest} />
              </div>
            </div>
            <button
              data-testid="start-walk-btn"
              onClick={start}
              disabled={starting || !dest}
              className="w-full bg-brand text-white rounded-md py-3 text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {starting ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} strokeWidth={1.8} />}
              Start walk & share
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <motion.div initial={{ scale: 0.98, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="rounded-xl p-5 border" style={{ background: st.bg, borderColor: `${st.color}40` }}
              data-testid="walk-status">
              <div className="flex items-center gap-2 font-semibold" style={{ color: st.color }}>
                {status?.status === "arrived" ? <Check size={18} /> : status?.status === "overdue" || status?.status === "stalled" ? <AlertTriangle size={18} /> : <MapPin size={18} />}
                {st.label}
              </div>
              {status?.status !== "arrived" && (
                <div className="text-sm text-muted-foreground mt-1">
                  {status?.eta_min ?? "…"} min to {status?.to_label || "your destination"} · {status?.remaining_m ?? "…"} m left
                </div>
              )}
            </motion.div>

            {status?.status !== "arrived" && (
              <div className="bg-white border border-border rounded-xl p-4">
                <span className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">Share this live link</span>
                <div className="flex items-center gap-2 mt-1.5">
                  <input data-testid="walk-share-link" readOnly value={shareUrl}
                    className="flex-1 text-sm rounded-md border border-border px-2.5 py-2 bg-secondary/40 truncate" />
                  <button data-testid="copy-walk-link" onClick={copy}
                    className="bg-brand text-white rounded-md px-3 py-2 hover:opacity-90 transition-colors">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            )}

            <button data-testid="arrive-btn" onClick={arrive}
              className="w-full bg-[#16a34a] text-white rounded-md py-3 text-sm font-semibold hover:opacity-90 transition-colors flex items-center justify-center gap-2">
              <Flag size={16} strokeWidth={1.8} /> I've arrived safely
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
